import { TodayItemDto } from "@growth/dto";
import { CONTENT_FORMATS, ContentFormat, Platform, PLATFORMS } from "@growth/schema";
import { Button, cn, Input } from "@growth/ui";
import { Check, LoaderCircle } from "lucide-react";
import { type FormEvent, useState } from "react";
import { Link } from "react-router-dom";

import { ErrorText, PageHeader } from "../components/page-header";
import { useActive } from "../lib/active";
import { apiErrorMessage } from "../lib/api-error";
import { useToday } from "../lib/calendar";
import { useTaskAction } from "../lib/content";
import { PLATFORM_STYLE, slotStatus } from "./calendar";
import { isTall } from "./content";

const day = (date: string, options: Intl.DateTimeFormatOptions) =>
  new Date(`${date}T00:00:00Z`).toLocaleDateString("en-AU", { timeZone: "UTC", ...options });

const dueLabel = (due: string | null, today: string) => {
  if (!due) return "not on the calendar";
  if (due === today) return "due today";
  if (due < today)
    return `was due ${day(due, { weekday: "short", day: "numeric", month: "short" })}`;
  return `due ${day(due, { weekday: "short", day: "numeric", month: "short" })}`;
};

/** What needs this person today, for the channels they own (or that nobody owns). */
export const TodayPage = () => {
  const { brand } = useActive();
  const today = useToday();
  const data = today.data;
  const total = data
    ? data.approve.length + data.decide.length + data.toPost.length + data.articles.length
    : 0;

  return (
    <section className="max-w-4xl space-y-6">
      <PageHeader
        eyebrow={`${brand?.name ?? ""}${data ? ` · ${day(data.today, { weekday: "long", day: "numeric", month: "long" })}` : ""}`}
        title={
          data
            ? total
              ? `${total} thing${total === 1 ? "" : "s"} need${total === 1 ? "s" : ""} you`
              : "Nothing needs you right now"
            : "Today"
        }
      >
        {data &&
          (data.myChannels.length
            ? `Your channels: ${data.myChannels.map((id) => PLATFORMS[id as Platform]?.name ?? id).join(", ")}, plus any channel nobody owns.`
            : "You see the channels nobody owns. Owners are set in Content setup.")}
      </PageHeader>
      {today.isError && <ErrorText>{apiErrorMessage(today.error)}</ErrorText>}
      {today.isPending && (
        <LoaderCircle className="size-5 animate-spin text-muted" aria-label="Loading" />
      )}
      {data && (
        <>
          <Section
            title="Approve or send back"
            tone="amber"
            items={data.approve}
            today={data.today}
            kind="approve"
          />
          <Section
            title="Needs a decision"
            tone="amber"
            items={data.decide}
            today={data.today}
            kind="decide"
          />
          <Section
            title="Approved, ready to post"
            tone="blue"
            items={data.toPost}
            today={data.today}
            kind="post"
          />
          {data.articles.length > 0 && (
            <div className="space-y-2">
              <h2 className="text-xs font-semibold uppercase tracking-widest text-amber-800">
                Blog drafts to review and publish · {data.articles.length}
              </h2>
              <ul className="divide-y divide-line rounded-xl border border-line bg-white">
                {data.articles.map((article) => (
                  <li
                    key={article.postId}
                    className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm"
                  >
                    <span className="text-ink">{article.title}</span>
                    <Link to={`/posts/${article.postId}`} className="text-accent underline">
                      Review and publish
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="space-y-2">
            <h2 className="text-xs font-semibold uppercase tracking-widest text-muted">
              Coming up this week
            </h2>
            {data.later.length === 0 ? (
              <p className="text-sm text-muted">
                Nothing planned for the next seven days.{" "}
                <Link to="/calendar" className="text-accent underline">
                  Plan a week
                </Link>
              </p>
            ) : (
              <ul className="divide-y divide-line rounded-xl border border-line bg-white">
                {data.later.map((slot) => (
                  <li
                    key={slot.id}
                    className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 text-sm"
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <span
                        className={cn(
                          "rounded px-1.5 py-0.5 text-[11px] font-semibold",
                          PLATFORM_STYLE[slot.channel],
                        )}
                      >
                        {PLATFORMS[slot.channel as Platform]?.short ?? slot.channel}
                      </span>
                      <span className="text-muted">
                        {day(slot.date, { weekday: "short", day: "numeric" })}
                      </span>
                      <span className="truncate">{slot.title}</span>
                    </span>
                    <span className="text-xs text-muted">{slotStatus(slot)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </section>
  );
};

const Section = ({
  title,
  tone,
  items,
  today,
  kind,
}: {
  title: string;
  tone: "amber" | "blue";
  items: TodayItemDto[];
  today: string;
  kind: "approve" | "decide" | "post";
}) =>
  items.length ? (
    <div className="space-y-3">
      <h2
        className={cn(
          "text-xs font-semibold uppercase tracking-widest",
          tone === "amber" ? "text-amber-800" : "text-accent",
        )}
      >
        {title} · {items.length}
      </h2>
      {items.map((item) => (
        <Item key={item.task.id} item={item} today={today} kind={kind} />
      ))}
    </div>
  ) : null;

const Item = ({
  item,
  today,
  kind,
}: {
  item: TodayItemDto;
  today: string;
  kind: "approve" | "decide" | "post";
}) => {
  const { atLeast } = useActive();
  const act = useTaskAction();
  const [url, setUrl] = useState("");
  const { piece, task } = item;
  const overdue = item.due !== null && item.due < today;

  const posted = (event: FormEvent) => {
    event.preventDefault();
    act.mutate({ kind: "posted", taskId: task.id, url });
  };

  return (
    <article className="flex gap-4 rounded-xl border border-line bg-white p-4">
      {piece.coverUrl && (
        <img
          src={piece.coverUrl}
          alt=""
          className={`${isTall(piece.channel) ? "aspect-[9/16]" : "aspect-[4/5]"} w-16 shrink-0 rounded border border-line object-cover`}
        />
      )}
      <div className="min-w-0 flex-1 space-y-2">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span
            className={cn("rounded px-1.5 py-0.5 font-semibold", PLATFORM_STYLE[piece.channel])}
          >
            {CONTENT_FORMATS[`${piece.channel}:${piece.format}` as ContentFormat] ?? piece.channel}
          </span>
          <span className={overdue ? "font-medium text-red-700" : "text-muted"}>
            {dueLabel(item.due, today)}
          </span>
          <span className="font-mono text-muted">{piece.code}</span>
        </div>
        <p className="text-[15px] leading-snug text-ink">{piece.hook || piece.idea}</p>
        {kind === "decide" && <p className="text-sm text-amber-800">{task.title}</p>}
        {task.busy ? (
          <p className="flex items-center gap-2 text-sm text-muted">
            <LoaderCircle className="size-4 animate-spin" /> Working on it…
          </p>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            {kind === "approve" && atLeast("REVIEWER") && (
              <Button
                disabled={act.isPending}
                onClick={() => act.mutate({ kind: "approve", taskId: task.id })}
              >
                <Check className="size-4" /> Approve
              </Button>
            )}
            {kind === "post" && atLeast("EDITOR") && (
              <form onSubmit={posted} className="flex flex-1 flex-wrap gap-2">
                <Input
                  type="url"
                  required
                  aria-label="Live post link"
                  placeholder="Paste the live post's link"
                  className="min-w-56 flex-1"
                  value={url}
                  onChange={(event) => setUrl(event.target.value)}
                />
                <Button type="submit" disabled={act.isPending}>
                  Mark posted
                </Button>
              </form>
            )}
            <Link to={`/content/${piece.id}`} className="text-sm text-accent underline">
              {kind === "post"
                ? "Open: text, files and posting steps"
                : kind === "approve"
                  ? "Open, edit or send back"
                  : "Open and decide"}
            </Link>
          </div>
        )}
        {act.isError && <ErrorText>{apiErrorMessage(act.error)}</ErrorText>}
      </div>
    </article>
  );
};
